import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { Tag } from "@msuite/ui/tag";
import type { NumberFormatVariant } from "@msuite/utils/numbers";
import { formatNumber } from "@msuite/utils/numbers";
import {
	getArticleQuantityStateColor,
	getArticleQuantityStateTranslationKey,
	getArticleSizeString,
	getLoadCarrierQuantityState,
} from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import { useLoadCarrier } from "@/hooks/use-load-carrier";
import type { LoadCarrierPanelBaseProps } from "../shared";

/**
 * What a load carrier is, what the yard holds of it, and what is already claimed.
 *
 * The state tag rides on the balance rather than on the orderable figure, which is where an
 * article's sits. A carrier's reservation counts what unfinished picking rounds have *promised*,
 * and a round's own promise grows as the picker fills it in, so grading the remainder would turn
 * the row amber because somebody is loading a truck. The thresholds below are thresholds on the
 * yard; the orderable figure is what a new order may still ask for.
 */
export function LoadCarrierGeneral({ loadCarrierId }: LoadCarrierPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { loadCarrier } = useLoadCarrier(loadCarrierId);

	/** Computed */
	const state = getLoadCarrierQuantityState(loadCarrier);

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<ContentCard
					title={t("general.general")}
					icon={icon("Container")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.name")}</DataGrid.Label>
							<DataGrid.Value>{guardString(loadCarrier?.name)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.description")}</DataGrid.Label>
							<DataGrid.Value>{guardString(loadCarrier?.description)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.load_carrier_size")}</DataGrid.Label>
							<DataGrid.Value>{getArticleSizeString(loadCarrier)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("calculations.weight")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(
									loadCarrier?.weight,
									loadCarrier?.weightUnit as NumberFormatVariant,
									0,
								)}
							</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>
			</Panels.Panel>
			<Panels.Panel flex={1}>
				<ContentCard
					title={t("stock.stock_balance")}
					icon={icon("Box")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.stock_balance")}</DataGrid.Label>
							<DataGrid.Value>
								<Tag
									color={getArticleQuantityStateColor(state)}
									variant="plain"
								>
									{formatNumber(loadCarrier?.stockBalance, "integer", 0)}
								</Tag>
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>
								{t("stock.article_quantity_state.article_quantity_state")}
							</DataGrid.Label>
							<DataGrid.Value>{t(getArticleQuantityStateTranslationKey(state))}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.reserved_quantity")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(loadCarrier?.reservedQuantity, "integer", 0)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.orderable_quantity")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(loadCarrier?.availableQuantity, "integer", 0)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.warning_stock_balance")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(loadCarrier?.warningStockBalance, "integer")}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.minimum_stock_balance")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(loadCarrier?.minimumStockBalance, "integer")}
							</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>
			</Panels.Panel>
		</Panels>
	);
}
